import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS, PRODUCTS } from "../data/navLinks.js";
import "./Navbar3.css";

const SCROLL_DELTA = 8;
const TOP_OFFSET = 10;

function useHideOnScrollDown() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (y <= TOP_OFFSET) {
        setHidden(false);
      } else if (Math.abs(y - lastY) >= SCROLL_DELTA) {
        setHidden(y > lastY);
      } else {
        return;
      }
      lastY = y;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return hidden;
}

/* Sections opt in with data-nav-theme="dark" | "light" | "live" (colours
   driven continuously by CSS vars, as Method does while its background ramps). Whichever tagged section
   is last in the DOM and on the navbar's midline decides the navbar colour. */
function useOnDark(headerRef) {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const header = headerRef.current;
    if (!header || !("IntersectionObserver" in window)) return;

    const visible = new Set();
    const compute = () => {
      let top = null;
      visible.forEach((el) => {
        if (!top || top.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) top = el;
      });
      setTheme(top?.dataset.navTheme ?? "light");
    };

    let io;
    const observe = () => {
      io?.disconnect();
      visible.clear();
      // a thin strip along the navbar's midline, so a section only counts once it
      // actually sits behind the navbar content, not as soon as its top edge touches it
      const mid = Math.round(header.offsetHeight / 2);
      const bottom = Math.max(0, window.innerHeight - mid - 1);
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
          compute();
        },
        { rootMargin: `-${mid}px 0px -${bottom}px 0px` }
      );
      document.querySelectorAll("[data-nav-theme]").forEach((el) => io.observe(el));
    };

    observe();
    window.addEventListener("resize", observe);
    // sections can flip their own theme while scrolling (e.g. Method), and pages that load their content
    // after the route change (a job page waiting for its data) add themed sections late: pick those up too
    let raf = 0;
    const mo = new MutationObserver((records) => {
      if (records.some((r) => r.type === "childList")) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => document.querySelectorAll("[data-nav-theme]").forEach((el) => io?.observe(el)));
      }
      compute();
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ["data-nav-theme"], childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", observe);
    };
  }, [pathname, headerRef]);

  return theme;
}

/* Which nav link matches where the visitor is: /careers by route, the home sections by scroll position
   (the last section whose top has passed 35% of the viewport). Null above the first section and on other pages. */
function useActiveLink() {
  const { pathname } = useLocation();
  const [section, setSection] = useState(null);

  useEffect(() => {
    if (pathname !== "/") return;
    const sections = BAR_LINKS.filter((l) => l.href.startsWith("/#")).map((l) => ({ href: l.href, id: l.href.slice(2) }));
    let ticking = false;
    const update = () => {
      ticking = false;
      const line = window.innerHeight * 0.35;
      let current = null;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= line) current = s.href;
      }
      setSection(current);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathname]);

  if (pathname.startsWith("/careers")) return "/careers";
  return pathname === "/" ? section : null;
}

/* One underline that slides to the active link and shrinks away when there is none. */
function useInk(navRef, active) {
  const [ink, setInk] = useState({ x: 0, y: 0, w: 0, on: false });

  useEffect(() => {
    const measure = () => {
      const link = active && navRef.current?.querySelector(`[data-href="${active}"]`);
      if (!link) return setInk((i) => (i.on ? { ...i, on: false } : i));
      // the underline spans the label, not the link's side padding
      const pad = parseFloat(getComputedStyle(link).paddingLeft) || 0;
      setInk({ x: link.offsetLeft + pad, y: link.offsetTop + link.offsetHeight - 5, w: link.offsetWidth - pad * 2, on: true });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active, navRef]);

  return ink;
}

const MOBILE = "(max-width: 767px)";
// the footer keeps Works and FAQs; the navbar drops Works and shows the team ("Minds") where FAQs was.
// Products sits right after Process.
const BAR_LINKS = NAV_LINKS.filter((l) => l.href !== "/#work").map((l) => (l.href === "/#faq" ? { label: "Minds", href: "/#team-section" } : l));
const PRODUCTS_AFTER = "/#process";

/* Phone menu: open/close state, page scroll lock, Escape, focus in and back out,
   and closing itself on navigation or when the screen grows past the phone layout. */
function useMobileMenu(buttonRef, panelRef) {
  const [open, setOpen] = useState(false);

  // links close it themselves; Back/Forward and growing past the phone layout close it here
  useEffect(() => {
    const close = () => setOpen(false);
    const mq = window.matchMedia(MOBILE);
    const onWide = () => !mq.matches && close();
    window.addEventListener("popstate", close);
    mq.addEventListener("change", onWide);
    return () => {
      window.removeEventListener("popstate", close);
      mq.removeEventListener("change", onWide);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const button = buttonRef.current;
    html.classList.add("is-menu-open");
    panelRef.current?.querySelector(".navbar3-sheet-link")?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      html.classList.remove("is-menu-open");
      document.removeEventListener("keydown", onKey);
      button?.focus({ preventScroll: true });
    };
  }, [open, buttonRef, panelRef]);

  return [open, setOpen];
}

/* "Products" dropdown: opens on hover (mouse) or click/Enter (touch, keyboard), closes on Escape,
   outside click, focus leaving, or choosing an item. */
function ProductLink({ product, ...props }) {
  // every product opens in a new tab; the router's Link leaves target="_blank" clicks to the browser
  const tab = { target: "_blank", rel: "noopener noreferrer" };
  return product.external ? <a href={product.href} {...tab} {...props} /> : <Link to={product.href} {...tab} {...props} />;
}

function ProductsMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        rootRef.current?.querySelector("button")?.focus({ preventScroll: true });
      }
    };
    const onDown = (e) => !rootRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  // hover only means "open" for a real mouse; on touch the click toggles it
  const onHover = (e, next) => e.pointerType === "mouse" && setOpen(next);

  return (
    <div
      ref={rootRef}
      className="navbar3-products"
      onPointerEnter={(e) => onHover(e, true)}
      onPointerLeave={(e) => onHover(e, false)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen(false)}
    >
      <button type="button" className="navbar3-link navbar3-products-btn" aria-expanded={open} aria-controls="products-menu" onClick={() => setOpen((o) => !o)}>
        Products
        <svg viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div id="products-menu" className={`navbar3-products-panel${open ? " is-open" : ""}`} inert={!open}>
        {PRODUCTS.map((p) => (
          <ProductLink key={p.href} product={p} className="navbar3-product" onClick={() => setOpen(false)}>
            <span className="navbar3-product-name">{p.label}</span>
            <span className="navbar3-product-note">{p.note}</span>
          </ProductLink>
        ))}
      </div>
    </div>
  );
}

export default function Navbar3() {
  const headerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const hidden = useHideOnScrollDown();
  const theme = useOnDark(headerRef);
  const [menuOpen, setMenuOpen] = useMobileMenu(buttonRef, panelRef);
  const { pathname } = useLocation();
  const navRef = useRef(null);
  const active = useActiveLink();
  const ink = useInk(navRef, active);

  return (
    <>
    <header
      ref={headerRef}
      className={`navbar3${hidden && !menuOpen ? " navbar3--hidden" : ""}${theme === "dark" ? " navbar3--on-dark" : ""}${theme === "live" ? " navbar3--live" : ""}${menuOpen ? " navbar3--menu" : ""}`}
    >
      <Link to="/" aria-current={pathname === "/" ? "page" : undefined} className="navbar3-brand" aria-label="Version3 home">
        <img className="navbar3-logo" src="/assets/logo/version3_logo_b.webp" alt="Version3" width="700" height="240" fetchPriority="high" />
      </Link>

      <nav className="navbar3-items" aria-label="Main" ref={navRef}>
        {BAR_LINKS.map((link) => (
          <Fragment key={link.href}>
            <Link
              to={link.href}
              data-href={link.href}
              className="navbar3-link"
              aria-current={active === link.href ? (link.href.startsWith("/#") ? "location" : "page") : undefined}
            >
              {link.label}
            </Link>
            {link.href === PRODUCTS_AFTER && <ProductsMenu />}
          </Fragment>
        ))}
        <span
          className={`navbar3-ink${ink.on ? " is-on" : ""}`}
          aria-hidden="true"
          style={{ "--x": `${ink.x}px`, "--y": `${ink.y}px`, "--w": `${ink.w}px` }}
        />
        <Link to="/#CTA-Form" className="navbar3-pill">
          <span className="navbar3-pill-bg" aria-hidden="true" />
          <span className="navbar3-pill-text">let's connect</span>
        </Link>
        <button
          ref={buttonRef}
          type="button"
          className="navbar3-burger"
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </nav>
    </header>

    <div
      ref={panelRef}
      id="mobile-menu"
      className={`navbar3-sheet${menuOpen ? " is-open" : ""}`}
      aria-hidden={!menuOpen}
      inert={!menuOpen}
    >
      <div className="navbar3-sheet-bar">
        <Link to="/" className="navbar3-brand" aria-label="Version3 home" onClick={() => setMenuOpen(false)}>
          <img className="navbar3-logo navbar3-sheet-logo" src="/assets/logo/version3_logo_b.webp" alt="" width="700" height="240" />
        </Link>
        <button type="button" className="navbar3-sheet-close" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <nav aria-label="Menu" className="navbar3-sheet-nav">
        {BAR_LINKS.map((link, i) => (
          <Link key={link.href} to={link.href} className="navbar3-sheet-link" aria-current={active === link.href ? "location" : undefined} style={{ "--i": i }} onClick={() => setMenuOpen(false)}>
            <span className="navbar3-sheet-num">{String(i + 1).padStart(2, "0")}</span>
            {link.label}
          </Link>
        ))}
        <p className="navbar3-sheet-group" style={{ "--i": BAR_LINKS.length }}>Products</p>
        {PRODUCTS.map((p, i) => (
          <ProductLink key={p.href} product={p} className="navbar3-sheet-link navbar3-sheet-product" style={{ "--i": BAR_LINKS.length + 1 + i }} onClick={() => setMenuOpen(false)}>
            {p.label}
          </ProductLink>
        ))}
      </nav>
      <Link to="/#CTA-Form" className="navbar3-sheet-cta" style={{ "--i": BAR_LINKS.length + PRODUCTS.length + 1 }} onClick={() => setMenuOpen(false)}>
        Start a project
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3.3 8h9.4M10 10.7 12.7 8M10 5.3 12.7 8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </div>
    </>
  );
}
